import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, ChevronDownIcon, ChevronRightIcon, CloseIcon } from './Icons';
import Pagination from './Pagination';
import CallButton from './CallButton';
import { getKnownAgents, normalizeNumber } from './callUtils';
import ParcelxNdr from './NdrParcelx';

const DEFAULT_PAGE_SIZE = 50;
// Jiffy is the only system of record for an NDR (see
// server/ivr/jiffyClient.js) — nothing is stored locally — so poll it the
// same way PaymentLinks.js/Refunds.js poll Razorpay. The server caches
// Jiffy's answer for 15s, shared across every agent's tab, so polling faster
// than that wouldn't show anything newer.
const AUTO_REFRESH_MS = 15000;

// Same tabs, in the same order, as Jiffy's own NDR screen.
const BUCKETS = [
  { key: 'action_required', label: 'Action Required', cardClass: 'critical' },
  { key: 'action_taken', label: 'Action Taken', cardClass: '' },
  { key: 'rto', label: 'RTO', cardClass: '' },
  { key: 'delivered', label: 'Delivered', cardClass: 'answered' },
  { key: 'all', label: 'All', cardClass: '' },
];

// Which tab an NDR belongs in. `jiffy_action_required` (set by the server
// from Jiffy's own status=action_required filter) is Jiffy's answer and wins
// outright. Otherwise shipment status decides RTO / Delivered, and only then
// the row's own `action_required` flag (a fallback for when the server's
// lookup failed).
const ndrBucket = (ndr) => {
  if (ndr.jiffy_action_required === true) return 'action_required';
  const status = String(ndr.shipment_status || '').toLowerCase();
  if (status.includes('rto')) return 'rto';
  if (status === 'delivered') return 'delivered';
  if (ndr.jiffy_action_required === false) return 'action_taken';
  const flag = ndr.action_required;
  return flag === true || flag === 'true' || flag === 1 ? 'action_required' : 'action_taken';
};

// Reuses the existing `.status` pill classes (App.css), same as PaymentLinks.js.
const statusClass = (ndr) => {
  const bucket = ndrBucket(ndr);
  if (bucket === 'delivered') return 'answered';
  if (bucket === 'rto') return 'attempted';
  if (bucket === 'action_required') return 'missed';
  return 'viewing';
};

// Jiffy sends timestamps as "YYYY-MM-DD HH:mm:ss" with no zone; they're shown
// as-is (no timezone shifting), just reformatted.
const parseJiffyDate = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const parsed = parseJiffyDate(value);
  if (!parsed) return '-';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  const parsed = parseJiffyDate(value);
  if (!parsed) return '-';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRupees = (value) => {
  const amount = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(amount)) return '-';
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const customerName = (ndr) => {
  const address = ndr.shipping_address || {};
  return [address.first_name, address.last_name].filter(Boolean).join(' ').trim();
};

const customerPhone = (ndr) => ndr.shipping_address?.phone_no || '';

const formatAddress = (address) =>
  [address?.address, address?.address_2, address?.city, address?.state, address?.pincode]
    .filter(Boolean)
    .join(', ');

const productNames = (ndr) => {
  if (!Array.isArray(ndr.products)) return '';
  return ndr.products
    .map((product) => (typeof product === 'string' ? product : product?.name || product?.product_name))
    .filter(Boolean)
    .join(', ');
};

// The expanded row's history is split into four attempt cards; every attempt
// past the third shares the last one.
const ATTEMPT_GROUPS = [
  { key: '1', label: 'Attempt 1', matches: (n) => n <= 1 },
  { key: '2', label: 'Attempt 2', matches: (n) => n === 2 },
  { key: '3', label: 'Attempt 3', matches: (n) => n === 3 },
  { key: '4+', label: 'Attempt 4+', matches: (n) => n >= 4 },
];

const eventAttempt = (event) => Number(event.attempt) || 1;

const eventTime = (event) => parseJiffyDate(event.event_time)?.getTime() || 0;

// "ndr" is the courier reporting a failed delivery; anything else (re-attempt,
// rto, …) is an action someone took in response.
const isNdrEvent = (event) => String(event.action || '').toLowerCase() === 'ndr';

const eventLabel = (event) => {
  const action = String(event.action || '').toLowerCase();
  if (action === 'ndr') return 'Delivery failed';
  if (action === 'rto') return 'RTO requested';
  if (action === 're-attempt' || action === 'reattempt') return 'Re-attempt requested';
  return event.action || 'Update';
};

// Attempt cards + a timeline of the selected attempt's events, newest first.
// Opens on the latest attempt that has any events, since that's the one an
// agent is calling about.
function NdrHistory({ ndr }) {
  const history = Array.isArray(ndr.action_history) ? ndr.action_history : [];
  const groups = ATTEMPT_GROUPS.map((group) => {
    const events = history
      .filter((event) => group.matches(eventAttempt(event)))
      .sort((a, b) => eventTime(b) - eventTime(a));
    return { ...group, events };
  });
  const latestGroup = [...groups].reverse().find((group) => group.events.length > 0);
  const [selectedKey, setSelectedKey] = useState(latestGroup?.key || '1');
  const selected = groups.find((group) => group.key === selectedKey) || groups[0];

  return (
    <div className="ndr-history">
      <div className="ndr-history-meta">
        <div>
          <span className="ndr-history-meta-label">Address</span>
          {formatAddress(ndr.shipping_address) || '-'}
        </div>
        {ndr.last_action_taken && (
          <div>
            <span className="ndr-history-meta-label">Last action taken</span>
            {ndr.last_action_taken}
          </div>
        )}
      </div>

      <div className="ndr-attempt-cards">
        {groups.map((group) => {
          const latest = group.events[0];
          const empty = group.events.length === 0;
          // A failed delivery with nothing done about it yet is what still
          // needs attention; once a re-attempt/RTO follows it, it's handled.
          const tone = empty ? '' : latest && isNdrEvent(latest) ? 'failed' : 'actioned';
          return (
            <button
              key={group.key}
              type="button"
              className={`ndr-attempt-card ${tone} ${selectedKey === group.key ? 'active' : ''}`}
              onClick={() => setSelectedKey(group.key)}
              disabled={empty}
            >
              <div className="ndr-attempt-card-top">
                <span className="ndr-attempt-card-label">{group.label}</span>
                <span className="ndr-attempt-card-count">
                  {group.events.length} {group.events.length === 1 ? 'event' : 'events'}
                </span>
              </div>
              {empty ? (
                <div className="ndr-attempt-card-empty">No attempt yet</div>
              ) : (
                <>
                  <div className="ndr-attempt-card-reason" title={latest.remarks || undefined}>
                    {latest.remarks || eventLabel(latest)}
                  </div>
                  <div className="ndr-attempt-card-date">{formatDateTime(latest.event_time)}</div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {selected.events.length === 0 ? (
        <div className="ndr-attempt-card-empty">No NDR history for {selected.label.toLowerCase()}.</div>
      ) : (
        <ol className="ndr-timeline">
          {selected.events.map((event, index) => (
            <li key={index} className={`ndr-timeline-item ${isNdrEvent(event) ? 'failed' : 'actioned'}`}>
              <div className="ndr-timeline-head">
                <span className="ndr-timeline-action">{eventLabel(event)}</span>
                <span className="ndr-timeline-by">by {event.action_by || '-'}</span>
                {selected.key === '4+' && <span className="ndr-timeline-by">· attempt {eventAttempt(event)}</span>}
                <span className="ndr-timeline-time">{formatDateTime(event.event_time)}</span>
              </div>
              {event.remarks && <div className="ndr-timeline-remarks">{event.remarks}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// Preset reasons per action; the chosen one is sent to Jiffy as the action's
// remarks (and shows up in the NDR history). "Other" lets the agent type one.
const ACTION_INFO = {
  're-attempt': {
    label: 'Re-attempt',
    title: 'Re-attempt delivery',
    button: 'Request re-attempt',
    note: 'The courier will try to deliver again (the next attempt).',
    reasons: [
      'Customer requested another delivery attempt',
      'Customer is available now, please deliver',
      'Customer wants delivery on a later date',
      'Address / landmark confirmed with customer',
      'Customer phone is reachable now',
      'Customer will keep COD amount ready',
    ],
  },
  rto: {
    label: 'RTO',
    title: 'Return to origin (RTO)',
    button: 'Mark as RTO',
    note: 'The courier will send these shipments back to the warehouse. This cannot be undone from here.',
    reasons: [
      'Customer refused delivery',
      'Customer cancelled the order',
      'Customer not reachable after multiple attempts',
      'Wrong / incomplete address',
      'Customer does not have COD amount',
    ],
  },
};
const OTHER_REASON = '__other__';

// Re-attempt / RTO can only be sent while Jiffy still wants an action.
const canTakeAction = (ndr) => ndrBucket(ndr) === 'action_required';

// "Actions" dropdown for one row. The menu is position: fixed under the
// button so the table's scroll container doesn't clip it; it closes on any
// outside click, scroll or resize.
function NdrActionsMenu({ ndr, onAction, onViewHistory }) {
  const buttonRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    if (!menuPos) return undefined;
    const close = (event) => {
      if (event.type === 'mousedown' && event.target.closest?.('.ndr-actions-menu, .ndr-actions-toggle')) return;
      setMenuPos(null);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuPos]);

  const toggle = () => {
    if (menuPos) {
      setMenuPos(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  const actionable = canTakeAction(ndr);
  const disabledTitle = actionable ? undefined : 'Jiffy is not waiting for an action on this shipment';
  const pick = (callback) => {
    setMenuPos(null);
    callback();
  };

  return (
    <>
      <button ref={buttonRef} type="button" className="btn btn-primary btn-small ndr-actions-toggle" onClick={toggle}>
        Actions
        <ChevronDownIcon width={12} height={12} />
      </button>
      {menuPos && (
        <div className="ndr-actions-menu" style={{ top: menuPos.top, right: menuPos.right }} role="menu">
          <button
            type="button"
            role="menuitem"
            disabled={!actionable}
            title={disabledTitle}
            onClick={() => pick(() => onAction(ndr, 're-attempt'))}
          >
            Re-attempt
          </button>
          <button
            type="button"
            role="menuitem"
            className="danger"
            disabled={!actionable}
            title={disabledTitle}
            onClick={() => pick(() => onAction(ndr, 'rto'))}
          >
            RTO
          </button>
          <button type="button" role="menuitem" onClick={() => pick(() => onViewHistory(ndr))}>
            View History
          </button>
        </div>
      )}
    </>
  );
}

// Re-attempt / RTO for one or more shipments (the row's Actions menu opens it
// with one, Bulk Update with every selected row). The agent picks the action
// and a reason; everything goes to Jiffy in one call (POST
// /api/ivr/ndr/actions). Jiffy can accept some AWBs and reject others, so the
// result is shown per order: if anything failed the modal stays open listing
// why, otherwise it closes.
function NdrActionModal({ ndrs, initialAction = 're-attempt', onClose, onDone }) {
  const { authFetch } = useAuth();
  const [action, setAction] = useState(initialAction);
  const [reasonChoice, setReasonChoice] = useState(ACTION_INFO[initialAction].reasons[0]);
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [failures, setFailures] = useState(null); // [{ awb_number, message }]

  const info = ACTION_INFO[action];
  const remarks = (reasonChoice === OTHER_REASON ? customReason : reasonChoice).trim();
  const isBulk = ndrs.length > 1;

  const switchAction = (next) => {
    setAction(next);
    setReasonChoice(ACTION_INFO[next].reasons[0]);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!remarks) {
      setError('Pick or type a reason.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const response = await authFetch('/api/ivr/ndr/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          remarks,
          items: ndrs.map((ndr) => ({ awb_number: ndr.awb_number, order_number: ndr.order_number })),
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Jiffy did not accept this action.');

      const successful = Array.isArray(body?.successful) ? body.successful : [];
      const failed = Array.isArray(body?.failed) ? body.failed : [];
      const what = `${info.label} sent to Jiffy for ${successful.length} order${successful.length === 1 ? '' : 's'}`;
      if (failed.length === 0) {
        onDone(`${what}.`, successful);
        return;
      }
      // Keep the modal open to show what didn't go through; the page still
      // refreshes and drops the ones that did from the selection.
      setFailures(failed);
      if (successful.length > 0) onDone(`${what}; ${failed.length} failed.`, successful, { keepOpen: true });
    } catch (err) {
      setError(err.message || 'Jiffy did not accept this action.');
    } finally {
      setSubmitting(false);
    }
  };

  const orderLabel = (awb) => {
    const ndr = ndrs.find((row) => String(row.awb_number) === String(awb));
    return ndr?.order_number || awb;
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={submitting ? undefined : onClose}>
      <div className="modal-panel modal-panel-compose" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{isBulk ? `Bulk update · ${ndrs.length} orders` : info.title}</h2>
            {!isBulk && (
              <p>
                Order {ndrs[0].order_number || '-'} · AWB {ndrs[0].awb_number} ·{' '}
                {customerName(ndrs[0]) || 'Unknown customer'}
              </p>
            )}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close" disabled={submitting}>
            <CloseIcon width={18} height={18} />
          </button>
        </div>

        {failures ? (
          <div className="modal-body">
            <div className="login-error">
              {failures.length} of {ndrs.length} order{ndrs.length === 1 ? '' : 's'} were not updated by Jiffy:
            </div>
            <ul className="ndr-bulk-failures">
              {failures.map((failure) => (
                <li key={failure.awb_number}>
                  <strong>{orderLabel(failure.awb_number)}</strong> (AWB {failure.awb_number}) — {failure.message}
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn-primary login-submit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="modal-body" onSubmit={handleSubmit}>
            <div className="ndr-action-switch" role="radiogroup" aria-label="Action">
              {Object.entries(ACTION_INFO).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={action === key}
                  className={`ndr-action-switch-option ${key === 'rto' ? 'danger' : ''} ${action === key ? 'active' : ''}`}
                  onClick={() => switchAction(key)}
                >
                  {value.label}
                </button>
              ))}
            </div>
            <p className={action === 'rto' ? 'ndr-action-note danger' : 'ndr-action-note'}>{info.note}</p>

            <div className="login-field">
              <span>Reason *</span>
              <div className="ndr-reason-list">
                {[...info.reasons, OTHER_REASON].map((reason) => (
                  <label key={reason} className={`ndr-reason ${reasonChoice === reason ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="ndr-reason"
                      checked={reasonChoice === reason}
                      onChange={() => setReasonChoice(reason)}
                    />
                    <span>{reason === OTHER_REASON ? 'Other (type a reason)' : reason}</span>
                  </label>
                ))}
              </div>
              {reasonChoice === OTHER_REASON && (
                <textarea
                  rows={2}
                  value={customReason}
                  onChange={(event) => setCustomReason(event.target.value)}
                  maxLength={500}
                  placeholder="Reason sent to Jiffy"
                  autoFocus
                />
              )}
            </div>

            <div className="login-field">
              <span>{isBulk ? `Orders (${ndrs.length})` : 'Order'}</span>
              <ul className="ndr-bulk-orders">
                {ndrs.map((ndr) => (
                  <li key={ndr.awb_number}>
                    <strong>{ndr.order_number || ndr.awb_number}</strong>
                    <span>{customerName(ndr) || '-'}</span>
                    <span className="stat-label">
                      Attempt {ndr.total_attempts ?? '-'}
                      {ndr.last_ndr_reason ? ` · ${ndr.last_ndr_reason}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {error && <div className="login-error">{error}</div>}
            <button
              type="submit"
              className={`btn ${action === 'rto' ? 'btn-danger' : 'btn-primary'} login-submit`}
              disabled={submitting || !remarks}
            >
              {submitting
                ? 'Sending to Jiffy…'
                : isBulk
                ? `${info.button} for ${ndrs.length} orders`
                : info.button}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Live view of Jiffy's NDR (non-delivery report) list — every shipment the
// courier failed to deliver — so agents can ring the customer straight from
// here and send the NDR action (re-attempt / RTO) to Jiffy without opening
// Jiffy's own panel.
function JiffyNdr({ calls = [], providerSwitch = null }) {
  const { authFetch } = useAuth();
  const [ndrs, setNdrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bucketFilter, setBucketFilter] = useState('action_required');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedId, setExpandedId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [actionTarget, setActionTarget] = useState(null); // { ndrs, action }
  const [actionNotice, setActionNotice] = useState('');
  // AWB numbers ticked for Bulk Update.
  const [selectedAwbs, setSelectedAwbs] = useState(() => new Set());

  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);

  const fetchNdrs = async ({ silent = false, fresh = !silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      // A manual Refresh (or a just-taken action) bypasses the server's
      // short cache; the background poll doesn't.
      const response = await authFetch(`/api/ivr/ndr${fresh ? '?refresh=1' : ''}`);
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        if (!silent) setNdrs([]);
        setError(body?.error || 'Failed to fetch NDRs.');
        return;
      }
      setError('');
      setNdrs(Array.isArray(body?.ndrs) ? body.ndrs : []);
      setLastUpdated(body?.fetched_at ? new Date(body.fetched_at) : new Date());
    } catch (err) {
      if (!silent) setNdrs([]);
      setError(err.message || 'Failed to fetch NDRs.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchNdrs();
    const interval = setInterval(() => fetchNdrs({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const bucketCounts = useMemo(() => {
    const counts = { all: ndrs.length, action_required: 0, action_taken: 0, rto: 0, delivered: 0 };
    ndrs.forEach((ndr) => {
      counts[ndrBucket(ndr)] += 1;
    });
    return counts;
  }, [ndrs]);

  // Jiffy's documented NDR rows carry no amounts or product names, so these
  // columns only appear if the live data actually includes them.
  const hasAmounts = useMemo(
    () => ndrs.some((ndr) => ndr.total_amount != null || ndr.collectable_amount != null),
    [ndrs]
  );
  const hasProducts = useMemo(() => ndrs.some((ndr) => productNames(ndr)), [ndrs]);

  const filteredNdrs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ndrs.filter((ndr) => {
      if (bucketFilter !== 'all' && ndrBucket(ndr) !== bucketFilter) return false;
      if (!term) return true;
      const haystack = [
        ndr.order_number,
        ndr.awb_number,
        ndr.courier_name,
        ndr.last_ndr_reason,
        customerName(ndr),
        customerPhone(ndr),
        ndr.shipping_address?.city,
        ndr.shipping_address?.pincode,
        productNames(ndr),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [ndrs, bucketFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [bucketFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredNdrs.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = filteredNdrs.slice((page - 1) * pageSize, page * pageSize);

  // Only rows Jiffy still wants an action on can be ticked, and only while
  // they're visible under the current tab/search: anything that drops out
  // (moved to Action Taken by a refresh, filtered away) is unticked, so Bulk
  // Update can never act on a row the agent can't see.
  const selectableAwbs = useMemo(
    () => new Set(filteredNdrs.filter((ndr) => ndr.awb_number && canTakeAction(ndr)).map((ndr) => String(ndr.awb_number))),
    [filteredNdrs]
  );
  useEffect(() => {
    setSelectedAwbs((prev) => {
      const next = new Set([...prev].filter((awb) => selectableAwbs.has(awb)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableAwbs]);

  const pageSelectable = pageRows.filter((ndr) => selectableAwbs.has(String(ndr.awb_number)));
  const pageAllSelected = pageSelectable.length > 0 && pageSelectable.every((ndr) => selectedAwbs.has(String(ndr.awb_number)));
  const pageSomeSelected = pageSelectable.some((ndr) => selectedAwbs.has(String(ndr.awb_number)));

  const toggleRow = (awb) => {
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      if (next.has(awb)) next.delete(awb);
      else next.add(awb);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      pageSelectable.forEach((ndr) => {
        if (pageAllSelected) next.delete(String(ndr.awb_number));
        else next.add(String(ndr.awb_number));
      });
      return next;
    });
  };

  const selectedNdrs = filteredNdrs.filter((ndr) => selectedAwbs.has(String(ndr.awb_number)));

  const handleActionDone = (message, successfulAwbs = [], { keepOpen = false } = {}) => {
    if (!keepOpen) setActionTarget(null);
    setActionNotice(message);
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      successfulAwbs.forEach((awb) => next.delete(String(awb)));
      return next;
    });
    fetchNdrs({ silent: true, fresh: true });
  };

  useEffect(() => {
    if (!actionNotice) return undefined;
    const timer = setTimeout(() => setActionNotice(''), 6000);
    return () => clearTimeout(timer);
  }, [actionNotice]);

  const columnCount = 10 + (hasAmounts ? 2 : 0) + (hasProducts ? 1 : 0);

  return (
    <>
      <div className="incoming-filters-bar">
        {providerSwitch}
        <button type="button" className="btn btn-secondary" onClick={() => fetchNdrs()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <input
          type="text"
          placeholder="Search by order no., customer, phone, AWB, courier, reason…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="search-input"
        />
        {lastUpdated && (
          <span className="stat-label">
            Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
      </div>

      <div className="stat-cards">
        {BUCKETS.map((bucket) => (
          <button
            key={bucket.key}
            type="button"
            className={`stat-card ${bucket.cardClass} ${bucketFilter === bucket.key ? 'active' : ''}`}
            onClick={() => setBucketFilter(bucket.key)}
          >
            <div className="stat-value">{bucketCounts[bucket.key]}</div>
            <div className="stat-label">{bucket.label}</div>
          </button>
        ))}
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}
      {actionNotice && <div className="ndr-action-notice">{actionNotice}</div>}

      {selectedNdrs.length > 0 && (
        <div className="ndr-selection-bar">
          <span>
            {selectedNdrs.length} order{selectedNdrs.length === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={() => setActionTarget({ ndrs: selectedNdrs, action: 're-attempt' })}
          >
            Bulk Update
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setSelectedAwbs(new Set())}>
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th className="ndr-check-cell">
                  <input
                    type="checkbox"
                    aria-label="Select all actionable orders on this page"
                    title={pageSelectable.length ? 'Select all on this page' : 'No orders here need an action'}
                    disabled={pageSelectable.length === 0}
                    checked={pageAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSomeSelected && !pageAllSelected;
                    }}
                    onChange={togglePage}
                  />
                </th>
                <th aria-label="Details" />
                <th>Order</th>
                <th>Customer</th>
                <th>Payment</th>
                {hasAmounts && <th>Total Amount</th>}
                {hasAmounts && <th>Collectable</th>}
                {hasProducts && <th>Products</th>}
                <th>Status</th>
                <th>Courier</th>
                <th>NDR Remark</th>
                <th>Last Activity</th>
                <th>Call</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount + 1} style={{ textAlign: 'center' }}>
                    {error ? 'Could not load NDRs.' : 'No NDRs match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((ndr) => {
                  const rowId = ndr.id || ndr.awb_number;
                  const expanded = expandedId === rowId;
                  const phone = customerPhone(ndr);
                  const awb = String(ndr.awb_number || '');
                  const selectable = selectableAwbs.has(awb);
                  const selected = selectedAwbs.has(awb);
                  return (
                    <React.Fragment key={rowId}>
                      <tr className={selected ? 'ndr-row-selected' : undefined}>
                        <td className="ndr-check-cell">
                          <input
                            type="checkbox"
                            aria-label={`Select order ${ndr.order_number || awb}`}
                            title={selectable ? undefined : 'Jiffy is not waiting for an action on this shipment'}
                            disabled={!selectable}
                            checked={selected}
                            onChange={() => toggleRow(awb)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => setExpandedId(expanded ? null : rowId)}
                            aria-label={expanded ? 'Hide details' : 'Show details'}
                            title={expanded ? 'Hide details' : 'Show address and NDR history'}
                          >
                            {expanded ? (
                              <ChevronDownIcon width={12} height={12} />
                            ) : (
                              <ChevronRightIcon width={12} height={12} />
                            )}
                          </button>
                        </td>
                        <td>
                          <div className="table-cell-strong">{ndr.order_number || '-'}</div>
                          <div className="stat-label">{formatDate(ndr.shipment_date)}</div>
                        </td>
                        <td>
                          <div>{customerName(ndr) || '-'}</div>
                          {phone && <div className="stat-label">{phone}</div>}
                        </td>
                        <td>
                          <span className={`status ${String(ndr.payment_method).toLowerCase() === 'cod' ? 'attempted' : 'answered'}`}>
                            {String(ndr.payment_method || '-').toUpperCase()}
                          </span>
                        </td>
                        {hasAmounts && <td>{formatRupees(ndr.total_amount)}</td>}
                        {hasAmounts && <td>{formatRupees(ndr.collectable_amount)}</td>}
                        {hasProducts && <td title={productNames(ndr)}>{productNames(ndr) || '-'}</td>}
                        <td>
                          <span className={`status ${statusClass(ndr)}`}>{ndr.shipment_status || '-'}</span>
                        </td>
                        <td>
                          <div>{ndr.courier_name || '-'}</div>
                          {ndr.awb_number && <div className="stat-label">{ndr.awb_number}</div>}
                        </td>
                        <td>
                          <div title={ndr.last_ndr_reason || undefined}>{ndr.last_ndr_reason || '-'}</div>
                          <div className="stat-label">Attempts: {ndr.total_attempts ?? '-'}</div>
                        </td>
                        <td>
                          <div>{ndr.last_action_by || '-'}</div>
                          <div className="stat-label">{formatDate(ndr.last_activity_date)}</div>
                        </td>
                        <td>
                          {phone ? (
                            <CallButton
                              agents={knownAgents}
                              customerNumber={normalizeNumber(phone)}
                              onCallTriggered={() => {}}
                            />
                          ) : (
                            <span title="No phone number on this shipment">-</span>
                          )}
                        </td>
                        <td>
                          <NdrActionsMenu
                            ndr={ndr}
                            onAction={(target, action) => setActionTarget({ ndrs: [target], action })}
                            onViewHistory={() => setExpandedId(rowId)}
                          />
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={2} />
                          <td colSpan={columnCount - 1}>
                            <NdrHistory ndr={ndr} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredNdrs.length}
      />

      {actionTarget && (
        <NdrActionModal
          ndrs={actionTarget.ndrs}
          initialAction={actionTarget.action}
          onClose={() => setActionTarget(null)}
          onDone={handleActionDone}
        />
      )}
    </>
  );
}

// The NDR page: Jiffy (above, unchanged) and, when the server has it enabled,
// ParcelX (NdrParcelx.js), picked with a switch in the filter bar. Without
// ParcelX — not configured, or an older server with no /ndr/providers — the
// page is the Jiffy view alone, exactly as before.
const PROVIDER_STORAGE_KEY = 'ndr_provider';

const readStoredProvider = () => {
  try {
    return window.localStorage.getItem(PROVIDER_STORAGE_KEY) || 'jiffy';
  } catch {
    return 'jiffy';
  }
};

function Ndr({ calls = [] }) {
  const { authFetch } = useAuth();
  const [parcelxEnabled, setParcelxEnabled] = useState(null); // null = still checking
  const [provider, setProvider] = useState(readStoredProvider);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/ivr/ndr/providers')
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled) return;
        const parcelx = Array.isArray(body?.providers) ? body.providers.find((p) => p.key === 'parcelx') : null;
        setParcelxEnabled(Boolean(parcelx?.enabled));
      })
      .catch(() => {
        if (!cancelled) setParcelxEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const choose = (next) => {
    setProvider(next);
    try {
      window.localStorage.setItem(PROVIDER_STORAGE_KEY, next);
    } catch {
      // Remembering the tab is a convenience; the page works without it.
    }
  };

  // Don't flash the Jiffy view for an agent who last used ParcelX.
  if (parcelxEnabled === null && provider === 'parcelx') return <div className="loading-state">Loading…</div>;

  if (!parcelxEnabled) return <JiffyNdr calls={calls} />;

  const providerSwitch = (
    <div className="customer-toggle" role="group" aria-label="NDR provider">
      <button type="button" className={provider === 'jiffy' ? 'active' : ''} onClick={() => choose('jiffy')}>
        Jiffy
      </button>
      <button type="button" className={provider === 'parcelx' ? 'active' : ''} onClick={() => choose('parcelx')}>
        ParcelX
      </button>
    </div>
  );

  return provider === 'parcelx' ? (
    <ParcelxNdr calls={calls} providerSwitch={providerSwitch} />
  ) : (
    <JiffyNdr calls={calls} providerSwitch={providerSwitch} />
  );
}

export default Ndr;
